import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

type StoredToken = { AccessToken?: string };

/**
 * Filter-dropdown tab. Mirrors the `?type=` query param expected by the BE.
 * Named distinctly from the unrelated `BetType` in `types/bets.ts` (the bet
 * kind enum: "1" | "2" | "4" | "5") to avoid semantic collision.
 */
export type BetFilterType = "sportsbook" | "casino";

export type BetFilterOption = {
  /** Stable key sent back to the BE in the `status` / `money_type` filter param. */
  value: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
};

export type BetFilterDropdowns = {
  statuses: BetFilterOption[];
  currencies: BetFilterOption[];
  /** Casino-only — empty for sportsbook. */
  providers: BetFilterOption[];
  /** Casino-only — scoped to `provider` if it was passed. */
  games: BetFilterOption[];
  /** Sportsbook-only — empty for casino. Values feed the `bet_types` list param. */
  bet_types: BetFilterOption[];
};

/** First segment of the `queryKey`; export so consumers can target invalidations. */
export const GET_BETS_FILTER_DROPDOWNS_QUERY_KEY = "GET_BETS_FILTER_DROPDOWNS";

const EMPTY_RESPONSE: BetFilterDropdowns = {
  statuses: [],
  currencies: [],
  providers: [],
  games: [],
  bet_types: [],
};

function normalizeOption(raw: unknown): BetFilterOption | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    const v = raw.trim();
    return v ? { value: v, label: v } : null;
  }
  if (typeof raw === "object") {
    const r = raw as { value?: unknown; label?: unknown };
    const value = r.value != null ? String(r.value).trim() : "";
    if (!value) return null;
    const label = r.label != null ? String(r.label).trim() : value;
    return { value, label: label || value };
  }
  return null;
}

function normalizeList(raw: unknown): BetFilterOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeOption)
    .filter((o): o is BetFilterOption => Boolean(o));
}

/**
 * GET /admin-panel-users/v1/bets/filter-dropdowns?type=<sportsbook|casino>
 *
 * Returns the status + currency options for the bets table filter row.
 * Tolerant of common BE envelopes (`{ data: { ... } }` vs flat) so the
 * dropdowns never crash on shape drift; falls back to empty arrays.
 *
 * The hook keys by `type`, so sportsbook and casino each get their own
 * cached entry. `staleTime` is 5 minutes — these lists rarely change and
 * BE derives them dynamically from transaction data.
 */
async function fetchBetFilterDropdowns(
  type: BetFilterType,
  provider?: string,
): Promise<BetFilterDropdowns> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  // BE filters games by sub_provider (the label), so pass it through verbatim.
  const params: Record<string, string> = { type };
  if (provider) params.provider = provider;

  const response = await instance.get(API_ROUTES.GET_BETS_FILTER_DROPDOWNS, {
    params,
  });
  const root = response?.data?.data ?? response?.data ?? null;
  if (!root || typeof root !== "object") return EMPTY_RESPONSE;

  const r = root as {
    statuses?: unknown;
    currencies?: unknown;
    providers?: unknown;
    games?: unknown;
    bet_types?: unknown;
  };
  return {
    statuses: normalizeList(r.statuses),
    currencies: normalizeList(r.currencies),
    providers: normalizeList(r.providers),
    games: normalizeList(r.games),
    bet_types: normalizeList(r.bet_types),
  };
}

export function useGetBetFilterDropdowns(
  type: BetFilterType,
  provider?: string,
) {
  return useQuery<BetFilterDropdowns, Error>({
    queryKey: [GET_BETS_FILTER_DROPDOWNS_QUERY_KEY, type, provider ?? ""],
    queryFn: () => fetchBetFilterDropdowns(type, provider),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export default useGetBetFilterDropdowns;
